import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Box, IconButton, InputAdornment, Paper, TextField, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Search, X, Users, UserPlus, Building2, Contact, FileText, Briefcase, FolderOpen, Loader2 } from "lucide-react"
import { searchApi, searchResultHref, visibleSearchResults } from "@crm-src/lib/search-api"
import type { SearchAccess, SearchResult } from "@crm-src/lib/search-api"
import { useResellerAccess } from "@crm-src/components/reseller"
import usePermissions from "@crm-src/hooks/usePermissions"
import { CRM_SEARCH_TYPES, CRM_SEARCH_TYPE_PAGE } from "@xop/dashboard-shared"

// Icon mapping for search result types
const iconMap: Record<string, React.ElementType> = {
  'users': Users,
  'user-plus': UserPlus,
  'building2': Building2,
  'contact': Contact,
  'file-text': FileText,
  'briefcase': Briefcase,
  'folder-open': FolderOpen,
}

// Type label mapping for display
const typeLabels: Record<string, string> = {
  'customer': 'Customers',
  'prospect': 'Prospects',
  'reseller': 'Resellers',
  'contact': 'Contacts',
  'invoice': 'Invoices',
  'quote': 'Quotes',
  'file': 'Files',
}

// Type accent palette tokens for tinted icon swatch + badge
const typeColors: Record<string, string> = {
  'customer': 'primary.main',
  'prospect': 'success.main',
  'reseller': 'secondary.main',
  'contact': 'warning.main',
  'invoice': 'warning.dark',
  'quote': 'info.main',
  'file': 'text.secondary',
}

export function GlobalSearch() {
  const navigate = useNavigate()
  // Reseller wall: a viewer without crm:resellers must not see "Resellers"
  // results in global search either. See components/reseller.tsx.
  const canViewResellers = useResellerAccess()
  // A result group shows only if its PAGE is one the viewer can open — the
  // same gate the sidebar uses, applied to the ONE type → page table that
  // crm-api's search gate mirrors (packages/shared/src/crmSearch.ts). Display
  // only: crm-api already left out every type the viewer cannot open.
  const { canAccessPage } = usePermissions()
  const flags = CRM_SEARCH_TYPES.map((type) =>
    type === "reseller" ? canViewResellers : canAccessPage(CRM_SEARCH_TYPE_PAGE[type]),
  )
  const flagsKey = flags.join(",")
  const access = useMemo<SearchAccess>(
    () => Object.fromEntries(CRM_SEARCH_TYPES.map((type, i) => [type, flags[i] === true])) as SearchAccess,
    // `flags` is rebuilt every render; its joined value is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flagsKey],
  )
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Debounced search function
  const performSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([])
      setIsOpen(false)
      return
    }

    setIsLoading(true)
    try {
      const response = await searchApi.search(searchQuery)
      // Reseller wall included: `access.reseller` is useResellerAccess(), so a
      // viewer without crm:resellers gets no reseller hits even if crm-api
      // (which also omits them via crm_resellers) ever sent one.
      setResults(visibleSearchResults(response.results, access))
      setIsOpen(true)
      setSelectedIndex(-1)
    } catch (error) {
      console.error('Search error:', error)
      setResults([])
    } finally {
      setIsLoading(false)
    }
  }, [access])

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Debounce the search
    timeoutRef.current = setTimeout(() => {
      performSearch(value)
    }, 300)
  }

  // Handle clear input
  const handleClear = () => {
    setQuery("")
    setResults([])
    setIsOpen(false)
    setSelectedIndex(-1)
    inputRef.current?.focus()
  }

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    navigate(searchResultHref(result.url))
    setIsOpen(false)
    setQuery("")
    setResults([])
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) {
      if (e.key === 'Escape') {
        handleClear()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev =>
          prev < results.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1)
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleResultClick(results[selectedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSelectedIndex(-1)
        break
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Group results by type for display
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = []
    }
    acc[result.type]!.push(result)
    return acc
  }, {} as Record<string, SearchResult[]>)

  // Flatten for keyboard navigation
  const flatResults = Object.values(groupedResults).flat()

  return (
    <Box sx={{ position: "relative", width: "100%", maxWidth: 448 }}>
      {/* Search Input */}
      <TextField
        fullWidth
        size="small"
        type="text"
        value={query}
        inputRef={inputRef}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (query.length >= 2 && results.length > 0) {
            setIsOpen(true)
          }
        }}
        placeholder="Search the CRM…"
        inputProps={{ "aria-label": "Search the CRM: customers, prospects, resellers, contacts, invoices, quotes and files" }}
        sx={{ "& .MuiOutlinedInput-root": { bgcolor: "surfaceSoft" } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box sx={{ color: "text.disabled", display: "flex" }}>
                <Search size={16} />
              </Box>
            </InputAdornment>
          ),
          endAdornment: isLoading ? (
            <InputAdornment position="end">
              <Box sx={{ color: "text.disabled", display: "flex", animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }}>
                <Loader2 size={16} />
              </Box>
            </InputAdornment>
          ) : query ? (
            <InputAdornment position="end">
              <IconButton type="button" size="small" onClick={handleClear} aria-label="Clear search" edge="end" sx={{ color: "text.disabled", "&:hover": { color: "text.secondary" } }}>
                <X size={16} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {/* Search Results Dropdown */}
      {isOpen && (
        <Paper
          ref={dropdownRef}
          elevation={8}
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            mt: 1,
            zIndex: 50,
            maxHeight: 400,
            overflowY: "auto",
            border: 1,
            borderColor: "divider",
          }}
        >
          {results.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center", color: "text.secondary", fontSize: 14 }}>
              No results found for "{query}"
            </Box>
          ) : (
            <Box sx={{ py: 1 }}>
              {Object.entries(groupedResults).map(([type, typeResults]) => (
                <Box key={type}>
                  {/* Type Header */}
                  <Typography
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      fontSize: 12,
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      bgcolor: "surfaceSoft",
                      position: "sticky",
                      top: 0,
                    }}
                  >
                    {typeLabels[type] || type}
                  </Typography>

                  {/* Results */}
                  {typeResults.map((result) => {
                    const Icon = iconMap[result.icon] || FileText
                    const globalIndex = flatResults.indexOf(result)
                    const isSelected = globalIndex === selectedIndex
                    const accent = typeColors[result.type] || "text.secondary"

                    return (
                      <Box
                        component="button"
                        type="button"
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleResultClick(result)}
                        sx={{
                          width: "100%",
                          px: 1.5,
                          py: 1.25,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          textAlign: "left",
                          border: 0,
                          cursor: "pointer",
                          bgcolor: isSelected ? (t) => alpha(t.palette.primary.main, 0.12) : "transparent",
                          color: isSelected ? "primary.dark" : "text.primary",
                          "&:hover": { bgcolor: isSelected ? (t) => alpha(t.palette.primary.main, 0.12) : "action.hover" },
                        }}
                      >
                        <Box
                          sx={{
                            height: 32,
                            width: 32,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            color: accent,
                            bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                          }}
                        >
                          <Icon size={16} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {result.title}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {result.subtitle}
                          </Typography>
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            fontSize: 12,
                            px: 1,
                            py: 0.25,
                            borderRadius: 999,
                            flexShrink: 0,
                            color: accent,
                            bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                          }}
                        >
                          {typeLabels[result.type] || result.type}
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              ))}
            </Box>
          )}

          {/* Footer with keyboard hints */}
          {results.length > 0 && (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderTop: 1,
                borderColor: "divider",
                bgcolor: "surfaceSoft",
                fontSize: 12,
                color: "text.disabled",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box component="span">
                <Box component="kbd" sx={{ px: 0.75, py: 0.25, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 0.5, fontSize: 10 }}>↑↓</Box> Navigate
              </Box>
              <Box component="span">
                <Box component="kbd" sx={{ px: 0.75, py: 0.25, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 0.5, fontSize: 10 }}>↵</Box> Select
              </Box>
              <Box component="span">
                <Box component="kbd" sx={{ px: 0.75, py: 0.25, bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 0.5, fontSize: 10 }}>Esc</Box> Close
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  )
}
